import React from "react";
import { message } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { useGetOrderByIdQuery, useUpdateOrderMutation } from "api/orderApi";
import Loader from "shared/Loader";
import {
  formatTaxRate,
  splitTotalIntoSubtotalAndTax,
} from "utils/checkoutPricing";
import {
  ActionButton,
  AddressText,
  BackButton,
  Card,
  CardTitle,
  ErrorState,
  GrandTotal,
  HeaderActions,
  HeaderLeft,
  InfoGrid,
  InfoItem,
  InfoLabel,
  InfoValue,
  InvoiceBox,
  InvoiceRow,
  MarketplaceBadge,
  PageContainer,
  PageHeader,
  PageSubtitle,
  PageTitle,
  ProductInfo,
  ProductMeta,
  ProductName,
  ProductPrice,
  ProductRow,
  ProductTable,
  ProductThumb,
  StatusBadge,
  StatusDot,
  SummaryRow,
  SummarySection,
  TrackLink,
  TwoColGrid,
} from "../styles";

const getStatusLabel = (status) => {
  if (status === "PAYMENT_PENDING") return "Payment Pending";
  if (status === "FULL_FILLED" || status === "DELIVERED") return "Fulfilled";
  if (status === "SHIPPED") return "In Transit";
  if (status === "PROCESSING" || status === "IN_PROGRESS" || status === "PENDING")
    return "Processing";
  if (status === "CANCELED") return "Disputed";
  return "Processing";
};

const getStatusTheme = (status) => {
  const label = getStatusLabel(status);
  if (label === "Payment Pending")
    return { bg: "#fce7f3", color: "#be185d", dot: "#be185d" };
  if (label === "Fulfilled") return { bg: "#dcfce7", color: "#16a34a", dot: "#16a34a" };
  if (label === "In Transit") return { bg: "#dbeafe", color: "#1d4ed8", dot: "#1d4ed8" };
  if (label === "Disputed") return { bg: "#fee2e2", color: "#dc2626", dot: "#dc2626" };
  return { bg: "#fef3c7", color: "#d97706", dot: "#d97706" };
};

const formatRecipientName = (addr) => {
  if (!addr) return null;
  const name = `${addr.firstName || ""} ${addr.lastName || ""}`.trim();
  return name || null;
};

const formatShippingAddress = (addr) => {
  if (!addr) return "No shipping address on file";
  const lines = [
    [addr.streetAddress, addr.apartmentNumber].filter(Boolean).join(", "),
    [addr.city, addr.state, addr.zip].filter(Boolean).join(", "),
    addr.country,
  ].filter(Boolean);
  return lines.length ? lines.join("\n") : "No shipping address on file";
};

const formatPaymentMethod = (order) => {
  const invoiceType = order?.invoice?.type;
  if (invoiceType) {
    if (invoiceType.toUpperCase() === "STRIPE") return "Credit / Debit Card (Stripe)";
    if (invoiceType.toUpperCase() === "PAYPAL") return "PayPal";
    return invoiceType;
  }
  if (order?.stripePaymentIntentId) return "Credit / Debit Card (Stripe)";
  if (order?.status === "PAYMENT_PENDING") return "Not yet paid";
  return "—";
};

const formatDate = (date) =>
  new Date(date).toLocaleDateString("en-US", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const OrderDetail = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { data: order, isLoading, isError } = useGetOrderByIdQuery(orderId);
  const [updateOrder, { isLoading: isUpdating }] = useUpdateOrderMutation();

  const handleCancelOrder = async () => {
    if (!order?.id) return;
    if (order.status === "DELIVERED") {
      message.error("Cannot cancel a delivered order");
      return;
    }
    try {
      const result = await updateOrder({ orderId: order.id, status: "CANCELED" });
      if (result?.data) {
        message.success("Order cancelled successfully");
      } else if (result?.error) {
        message.error(result?.error?.data?.message || "Failed to cancel order");
      }
    } catch {
      message.error("Error cancelling order");
    }
  };

  const handlePayOrder = () => {
    navigate("/order_payment", {
      state: {
        existingOrderId: order.id,
        totalAmount: order.amount,
        taxRate: order.taxRate,
        marketplace: (order.marketplace || "SHOPIFY").toUpperCase(),
      },
    });
  };

  const handlePrintInvoice = () => {
    window.print();
  };

  if (isLoading) return <Loader isLoading />;

  if (isError || !order) {
    return (
      <PageContainer>
        <BackButton type="button" onClick={() => navigate("/my-orders")}>
          <ArrowLeftOutlined /> Back to My Orders
        </BackButton>
        <ErrorState>
          <p>Unable to load order details. The order may not exist or you may not have access.</p>
          <ActionButton type="button" onClick={() => navigate("/my-orders")}>
            Return to My Orders
          </ActionButton>
        </ErrorState>
      </PageContainer>
    );
  }

  const statusTheme = getStatusTheme(order.status);
  const { subtotal, tax, total } = splitTotalIntoSubtotalAndTax(
    order.amount,
    order.taxRate
  );
  const marketplace = (order.marketplace || "UNKNOWN").toString().toUpperCase().replaceAll("_", " ");
  const invoice = order.invoice;

  return (
    <PageContainer>
      <BackButton type="button" onClick={() => navigate("/my-orders")}>
        <ArrowLeftOutlined /> Back to My Orders
      </BackButton>

      <PageHeader>
        <HeaderLeft>
          <PageTitle>Order #{order.orderNumber ?? order.orderTrackingId ?? order.id?.slice(0, 8)}</PageTitle>
          <PageSubtitle>
            Placed on {formatDate(order.created)} &nbsp;·&nbsp;{" "}
            <MarketplaceBadge>{marketplace}</MarketplaceBadge>
          </PageSubtitle>
        </HeaderLeft>
        <HeaderActions>
          {order.status === "PAYMENT_PENDING" && (
            <ActionButton type="button" className="primary" onClick={handlePayOrder}>
              Pay Now
            </ActionButton>
          )}
          {(order.status === "PENDING" || order.status === "PAYMENT_PENDING") && (
            <ActionButton type="button" className="danger" onClick={handleCancelOrder}>
              Cancel Order
            </ActionButton>
          )}
          {invoice && (
            <ActionButton type="button" className="outline" onClick={handlePrintInvoice}>
              Print Invoice
            </ActionButton>
          )}
        </HeaderActions>
      </PageHeader>

      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <CardTitle style={{ margin: 0 }}>Order Status</CardTitle>
          <StatusBadge $bg={statusTheme.bg} $color={statusTheme.color}>
            <StatusDot $dot={statusTheme.dot} />
            {getStatusLabel(order.status)}
          </StatusBadge>
        </div>
        <InfoGrid style={{ marginTop: 16 }}>
          <InfoItem>
            <InfoLabel>Order ID</InfoLabel>
            <InfoValue>#{order.orderNumber ?? order.orderTrackingId ?? order.id}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Order Date</InfoLabel>
            <InfoValue>{formatDate(order.created)}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Marketplace</InfoLabel>
            <InfoValue>{marketplace}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Payment Method</InfoLabel>
            <InfoValue>{formatPaymentMethod(order)}</InfoValue>
          </InfoItem>
        </InfoGrid>
      </Card>

      <Card>
        <CardTitle>Items ({order.orderItems?.length || 0})</CardTitle>
        <ProductTable>
          {order.orderItems?.map((item) => {
            const product = item.product;
            const unitPrice = Number(item.price || product?.price || 0);
            const lineTotal = unitPrice * Number(item.quantity || 1);
            return (
              <ProductRow key={item.id}>
                <ProductThumb $img={product?.images?.[0]?.url}>
                  {!product?.images?.[0]?.url ? "📦" : ""}
                </ProductThumb>
                <ProductInfo>
                  <ProductName>{product?.name || "Product"}</ProductName>
                  <ProductMeta>
                    {item.variant?.name && `${item.variant.name} · `}
                    Qty: {item.quantity || 1}
                    {product?.sku && ` · SKU: ${product.sku}`}
                  </ProductMeta>
                </ProductInfo>
                <ProductPrice>${lineTotal.toFixed(2)}</ProductPrice>
              </ProductRow>
            );
          })}
        </ProductTable>

        <SummarySection>
          <SummaryRow>
            <span>Subtotal</span>
            <span>${subtotal.toFixed(2)}</span>
          </SummaryRow>
          <SummaryRow>
            <span>Tax ({formatTaxRate(order.taxRate)})</span>
            <span>${tax.toFixed(2)}</span>
          </SummaryRow>
          {invoice?.shippingFee > 0 && (
            <SummaryRow>
              <span>Shipping</span>
              <span>${Number(invoice.shippingFee).toFixed(2)}</span>
            </SummaryRow>
          )}
          <GrandTotal>
            <span>Total</span>
            <span>${total.toFixed(2)}</span>
          </GrandTotal>
        </SummarySection>
      </Card>

      <TwoColGrid>
        <Card>
          <CardTitle>Shipping Address</CardTitle>
          {formatRecipientName(order.shippingAddress) && (
            <AddressText style={{ fontWeight: 600, color: "#111", marginBottom: 8 }}>
              {formatRecipientName(order.shippingAddress)}
            </AddressText>
          )}
          <AddressText style={{ whiteSpace: "pre-line" }}>
            {formatShippingAddress(order.shippingAddress)}
          </AddressText>
          {order.shippingAddress?.phoneNumber && (
            <AddressText style={{ marginTop: 8, color: "#9ca3af", fontSize: 13 }}>
              Phone: {order.shippingAddress.phoneNumber}
            </AddressText>
          )}
        </Card>

        <Card>
          <CardTitle>Tracking</CardTitle>
          {order.carrierTrackingNumber ? (
            <>
              {order.carrier && (
                <InfoValue style={{ marginBottom: 6 }}>Carrier: {order.carrier}</InfoValue>
              )}
              <InfoValue style={{ marginBottom: 6 }}>
                Tracking #: {order.carrierTrackingNumber}
              </InfoValue>
              {order.shippedAt && (
                <InfoValue style={{ marginBottom: 6, color: "#9ca3af", fontSize: 13 }}>
                  Shipped: {formatDate(order.shippedAt)}
                </InfoValue>
              )}
              {order.trackingUrl && (
                <TrackLink href={order.trackingUrl} target="_blank" rel="noopener noreferrer">
                  Track package →
                </TrackLink>
              )}
            </>
          ) : (
            <AddressText>Awaiting tracking information from supplier.</AddressText>
          )}
        </Card>
      </TwoColGrid>

      {invoice && (
        <Card>
          <CardTitle>Payment Invoice</CardTitle>
          <InvoiceBox>
            <InvoiceRow>
              <span>Invoice ID</span>
              <span style={{ fontWeight: 600, color: "#111" }}>{invoice.id}</span>
            </InvoiceRow>
            <InvoiceRow>
              <span>Status</span>
              <span style={{ fontWeight: 600, color: "#111" }}>{invoice.status}</span>
            </InvoiceRow>
            <InvoiceRow>
              <span>Payment Method</span>
              <span style={{ fontWeight: 600, color: "#111" }}>{formatPaymentMethod(order)}</span>
            </InvoiceRow>
            <InvoiceRow>
              <span>Invoice Date</span>
              <span style={{ fontWeight: 600, color: "#111" }}>
                {invoice.created ? formatDate(invoice.created) : "—"}
              </span>
            </InvoiceRow>
            <InvoiceRow>
              <span>Gross Amount</span>
              <span style={{ fontWeight: 600, color: "#111" }}>
                ${Number(invoice.grossAmount || order.amount).toFixed(2)}
              </span>
            </InvoiceRow>
            {invoice.tax && (
              <InvoiceRow>
                <span>Tax</span>
                <span style={{ fontWeight: 600, color: "#111" }}>{invoice.tax}</span>
              </InvoiceRow>
            )}
            <InvoiceRow style={{ borderTop: "1px solid #e5e7eb", marginTop: 8, paddingTop: 10 }}>
              <span style={{ fontWeight: 700, color: "#111" }}>Total Paid</span>
              <span style={{ fontWeight: 700, color: "#e8521a", fontSize: 15 }}>
                ${total.toFixed(2)}
              </span>
            </InvoiceRow>
          </InvoiceBox>
        </Card>
      )}

      {order.status === "PAYMENT_PENDING" && !invoice && (
        <Card>
          <CardTitle>Payment</CardTitle>
          <AddressText>
            This order is awaiting payment. Click &ldquo;Pay Now&rdquo; above to complete your purchase.
          </AddressText>
        </Card>
      )}

      <Loader isLoading={isUpdating} />
    </PageContainer>
  );
};

export default OrderDetail;
